/***********************************************************************
 * Similar component to order_summary.jsx. The reason for this component
 * instead of using the already existing one is because this one
 * requires more customizations. This component a detailed
 * view of every item in the cart and its basic metadata.
 * It displays different totals for the order and it
 * allows for discount code model.
 *
 * It is at checkout, login and cart pages. Pass fromCheckout or fromLogin
 * props accordingly. It defaults to Cart.
 * **************************************
 *
 * @format */

import {DiscountForm} from 'frontier/cart/components/discounts/discount_form';
import {prettyProductLabel} from 'frontier/ds/quoter/functions/format';
import {PRODUCT_TYPE_GRAPHIC_DESIGN} from 'frontier-constants';
import {Button, Icon} from '@sttark/design-system';
import NumberFormat from 'react-number-format';
import './cart_overview_summary.sass';
import React from 'react';
import {CartCheckoutContext} from 'frontier/cart/components/cart/cart_checkout_context';

export const CartOverviewSummary = props => {
    let orders = [];
    let cart = {};

    // context does not exist in the /cart page, thus, the 'cart' prop containing the info is passed.
    // all other instances should default to context
    if (props.cart) {
        orders = props.cart.orders;
        cart = props.cart;
    } else {
        const ctx = React.useContext(CartCheckoutContext);
        cart = ctx.cart;
        orders = ctx.orders;
    }

    return (
        <div className="summary-container">
            {/* title */}
            <h3 className="summary-container_title summary-title font-heading-xl">Summary</h3>

            {/* items list*/}
            <div className="summary-container_items-list">
                {orders.map((item, index) => {
                    // check if item is graphic design
                    let itemLabel =
                        item.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN
                            ? item.product_type_label
                            : item.size_description;
                    itemLabel = prettyProductLabel(itemLabel, item.product_type_id);

                    const itemQty =
                        item.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN
                            ? item.graphic_design_order_details.design_type_id === 3
                                ? 1 // custom designs show 0 for design_qty and null for total_qty
                                : item.design_qty
                            : item.duplex_aware_total_qty;

                    return (
                        <div
                            key={index}
                            className="items-list_item d-flex align-items-end justify-content-start">
                            <div className="item_title-desc w-100">
                                <h5 className="summary-container_text flex-noshrink">
                                    {itemLabel}
                                </h5>
                                <p className="summary-container_text flex-noshrink">
                                    Qty:{' '}
                                    <NumberFormat
                                        value={itemQty}
                                        thousandSeparator={true}
                                        fixedDecimalScale={true}
                                        displayType="text"
                                        decimalScale={0}
                                        prefix=""
                                    />
                                </p>
                            </div>
                            <p className="summary-container_text text-end d-flex align-items-end justify-content-end">
                                <NumberFormat
                                    value={item.quoted_total}
                                    thousandSeparator={true}
                                    fixedDecimalScale={true}
                                    displayType="text"
                                    decimalScale={0}
                                    prefix="$"
                                />
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* discount form */}
            {props.fromCheckout && <DiscountForm />}

            {/* Shipping Total */}
            {(props.fromCheckout || props.fromLogin) && cart.shipping && (
                <div className="shipping-total d-flex align-items-start justify-content-between mb-1">
                    <h5 className="summary-container_text flex-shrink-0">Shipping</h5>
                    <p className="summary-container_text">
                        {!cart.saving && (
                            <NumberFormat
                                value={parseInt(cart.shipping ?? 0) + parseInt(cart.duties ?? 0)}
                                thousandSeparator={true}
                                fixedDecimalScale={true}
                                displayType="text"
                                decimalScale={0}
                                prefix="$"
                            />
                        )}
                        {cart.saving && <p className="pe-3 text-end">$...</p>}
                    </p>
                </div>
            )}

            {(props.fromCheckout || props.fromLogin) && cart.tax && (
                <div className="shipping-total d-flex align-items-start justify-content-between mb-4">
                    <h5 className="summary-container_text flex-shrink-0">Tax</h5>
                    <p className="summary-container_text">
                        {!cart.saving && (
                            <NumberFormat
                                value={parseInt(cart.tax)}
                                thousandSeparator={true}
                                fixedDecimalScale={true}
                                displayType="text"
                                decimalScale={0}
                                prefix="$"
                            />
                        )}
                        {cart.saving && <p className="pe-3 text-end">$...</p>}
                    </p>
                </div>
            )}

            {/* Total */}
            <div className="summary-container_total total-title px-0 tw-pt-0 d-flex align-items-center justify-content-between">
                <h3 className="summary-container_title">Total</h3>
                <h3 className="summary-container_title">
                    <NumberFormat
                        value={props.fromCheckout ? cart.total : cart.subtotal}
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={0}
                        displayType="text"
                        prefix="$"
                    />
                </h3>
            </div>

            {/* ctas */}
            {!props.fromCheckout && !props.fromLogin && <CTAS />}

            {/* get help */}
            <GetHelp />
        </div>
    );
};

export function CTAS() {
    return (
        <div className="summary-container_ctas d-flex align-items-center justify-content-center">
            <div>
                <Button
                    className="ctas_cta cta_secondary d-flex align-items-center justify-content-center w-100"
                    url="/orders/custom-labels"
                    label="Back to Quoter"
                    secondary
                    large
                />
            </div>
            <div>
                <Button
                    className="ctas_cta cta_primary d-flex align-items-center justify-content-center w-100 color-structure-white"
                    iconRight="navigateright"
                    label="Checkout"
                    url="/carts"
                    primary
                    large
                />
            </div>
        </div>
    );
}

export function GetHelp() {
    /* grabs the chat now button from the DOM injected
by the SDK and adds click event
*/
    const handleChatNow = () => {
        let chatButton = document.querySelector('.BeaconFabButtonFrame');
        chatButton =
            chatButton.lastChild.contentDocument || chatButton.lastChild.contentWindow.document;
        chatButton = chatButton.querySelector('.frame-content');
        chatButton = chatButton.firstChild;
        if (chatButton) chatButton.click();
    };

    return (
        <div className="summary-container_get-help m-0">
            <h3 className="summary-container_title get-help-title">Get Help</h3>
            <p>Reach out to our Customer Care team!</p>

            <div className="get-help_last-line d-flex align-items-center justify-content-start">
                <div className="get-gelp_icon">
                    <Icon name="phone" color="black" />
                </div>
                <p className="get-help_call-now">(877) 277-4682</p>
                <div className="get-gelp_icon">
                    <Icon color="black" name="chat" small />
                </div>
                <p className="chat-now-btn" onClick={handleChatNow}>
                    Chat Now
                </p>
            </div>
        </div>
    );
}
